<template>
  <div class="finish-types-component finishtypes-finishes-container">
    <el-row>
      <el-table
        ref="table"
        v-loading="loading"
        :data="tableData"
        style="width: 100%;margin-bottom: 20px;"
        row-key="treeIdentifier"
        border
        height="575"
        :row-class-name="tableRowClassName"
        :cell-class-name="tableCellClassName"
        default-expand-all
        highlight-current-row
        :tree-props="{ children: 'finishes' }"
        @row-dblclick="rowDblClick"
        @current-change="handleCurrentChange"
      >
        <el-table-column :min-width="150" width="300">
          <template slot="header" slot-scope="{}">
            {{ $t('productGroup.finishTypes') }}
          </template>
          <template slot-scope="{ row }">
            <el-checkbox
              v-if="showCheckboxes"
              v-model="row.checked"
              :indeterminate="row.indeterminate"
              @change="handleCheckboxChange(row, $event)"
            />
            <span class="checkbox-label"> {{ row[selectionLabelField] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('finish.quantityFormula')" width="300">
          <template slot-scope="{ row }">
            <MDinput
              v-if="mustShowFormula(row)"
              v-model="row.quantityFormula"
              type="text"
              :readonly="readonly"
              @input="handleInputFormula(row, $event)"
            />
          </template>
        </el-table-column>
        <el-table-column
          v-for="column in columns"
          :key="column.field"
          :prop="column.field"
          :width="column.width"
          :min-width="column.minWidth"
          :align="column.align"
          :header-align="column.headerAlign"
          :label="column.label ? $t(column.label) : $t(`common.${column.field}`)"
          :class-name="column.field"
          :formatter="column.formatter"
        >
          <template slot-scope="scope">
            <span
              v-html="
                column.formatter
                  ? column.formatter(scope.row, scope.column, scope.row[column.field])
                  : scope.row[column.field]
              "
            />
          </template>
        </el-table-column>
      </el-table>
    </el-row>
  </div>
</template>

<script>
import MDinput from '@/components/MDinput';
import { deepClone } from '@/utils';
import { QuantityCalculationMethodOption } from '@/utils/constants';

const SelectionType = Object.freeze({
  None: 0,
  Partly: 1,
  All: 2,
  Ignore: 3
});

const selectionsContainAnySelected = childSelections =>
  childSelections.some(selectionType => selectionType === SelectionType.Partly || selectionType === SelectionType.All);

const selectionsContainNoneOrPartlySelected = childSelections =>
  childSelections.some(selectionType => selectionType === SelectionType.None || selectionType === SelectionType.Partly);

const getSelectTypeByChildSelections = childSelections => {
  if (selectionsContainAnySelected(childSelections)) {
    if (selectionsContainNoneOrPartlySelected(childSelections)) {
      return SelectionType.Partly;
    } else {
      return SelectionType.All;
    }
  } else {
    return SelectionType.None;
  }
};
function valueHasChanged(newValue, oldValue) {
  if (!Array.isArray(newValue) || !Array.isArray(oldValue)) {
    return true; // Mutations non array to non array are covered by Vue reactivity
  }
  // Quick comparison to prevent rendering too many times when array reference has changed
  return JSON.stringify(newValue) !== JSON.stringify(oldValue);
}

function iterateThroughNode(parentNode, callback, parents) {
  if (!parents) {
    parents = [];
  }
  const cb_response = callback(parentNode, parents);
  if (cb_response !== false && parentNode.finishes) {
    parents.push(parentNode);
    parentNode.finishes.forEach(child => iterateThroughNode(child, callback, parents));
  }
}

function iterateThroughNodes(parentNodes, callback, parents) {
  parentNodes.forEach(parentNode => iterateThroughNode(parentNode, callback, parents));
}

export default {
  components: {
    MDinput
  },
  props: {
    value: Array,
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    initialValue: Array,
    selectionLabelField: {
      type: String,
      default: 'description'
    },
    columns: {
      default() {
        return [
          { field: 'code', width: 160, label: 'common.code' },
          { field: 'defaultQuantityFormula', label: 'productGroup.defaultFinishQuantityFormula', minWidth: 200 }
        ];
      },
      type: Array
    }
  },
  data() {
    return {
      loading: false,
      tableData: []
    };
  },
  computed: {
    showCheckboxes() {
      return !this.readonly;
    }
  },
  watch: {
    value(value, oldval) {
      if (Array.isArray(value) && valueHasChanged(value, oldval)) {
        const lines = value.slice(0);
        this.tableData = deepClone(lines);
        this.updateTable();
        if (oldval === null) {
          this.emitInit();
          this.emitValues();
        }
      }
    }
  },
  mounted() {
    this.$emit('requesterror', { code: 'error.input.quantityformula', action: this.showFormulaErrors });
  },
  methods: {
    showFormulaErrors(errorCode, errorMessage) {
      this.$store.dispatch('notify/toast', {
        message: this.$i18n.t('validation.error.invalidFormula', { errorMessage })
      });
    },
    handleCheckboxChange(row, selected) {
      this.setRowPropertiesAccordingToSelected(row, selected);
      if (row.finishes) {
        this.setArrayRowPropertiesAccordingToSelected(row.finishes, selected);
      }
      this.updateCheckboxes();
      this.emitValues();
    },
    setArrayRowPropertiesAccordingToSelected(array, selected) {
      iterateThroughNodes(array, row => {
        this.setRowPropertiesAccordingToSelected(row, selected);
      });
    },
    setRowPropertiesAccordingToSelected(row, selected) {
      if (this.isFinishType(row)) {
        row.linkedYN = selected;
      } else {
        row.applicableYN = selected;
      }
    },
    selectRow(row) {
      this.$set(row, 'checked', true);
      this.$set(row, 'indeterminate', false);
    },
    setIndeterminateSelect(row) {
      this.$set(row, 'checked', false);
      this.$set(row, 'indeterminate', true);
    },
    unselectRow(row) {
      this.$set(row, 'checked', false);
      this.$set(row, 'indeterminate', false);
    },
    setQuantityFormula(row, quantityFormula) {
      this.$set(row, 'quantityFormula', quantityFormula);
      this.$set(row, 'quantityFormula', quantityFormula);
    },
    handleInputFormula(row, quantityFormula) {
      row.quantityFormula = quantityFormula;
      this.emitValues();
    },
    handleCurrentChange(val) {
      this.currentRow = val;
    },
    rowDblClick(row) {
      if (this.onRowDblClick) {
        this.onRowDblClick(row);
      } else {
        if (this.disabled || this.readonly) {
          return;
        }
        if (row.selectionType === SelectionType.None || row.selectionType === SelectionType.Partly) {
          this.handleCheckboxChange(row, true);
        } else if (row.selectionType === SelectionType.All) {
          this.handleCheckboxChange(row, false);
        }
      }
    },
    updateCheckboxForRow(row) {
      let currentSelectionType = SelectionType.Ignore;
      if (row.finishes) {
        const childSelections = row.finishes.reduce((selections, childRow) => {
          selections.push(this.updateCheckboxForRow(childRow));
          return selections;
        }, []);
        currentSelectionType = getSelectTypeByChildSelections(childSelections);
      } else {
        if ((this.isFinishType(row) && row.linkedYN) || (this.isFinish(row) && row.applicableYN)) {
          currentSelectionType = SelectionType.All;
        } else {
          currentSelectionType = SelectionType.None;
        }
      }

      this.updateSelectionByType(row, currentSelectionType);
      return currentSelectionType;
    },
    updateSelectionByType(row, selectionType) {
      switch (selectionType) {
        case SelectionType.Partly:
          this.setIndeterminateSelect(row);
          break;
        case SelectionType.All:
          this.selectRow(row);
          break;
        case SelectionType.None:
        default:
          this.unselectRow(row);
      }
      this.$set(row, 'selectionType', selectionType);
    },
    updateCheckboxes() {
      this.tableData.forEach(tableRow => {
        this.updateCheckboxForRow(tableRow);
      });
    },
    updateTable() {
      this.updateCheckboxes();
      this.triggerUpdate();
    },
    emitValues() {
      this.$emit('input', deepClone(this.tableData));
    },
    emitInit() {
      this.$emit('init', deepClone(this.tableData));
    },
    setCurrent(row) {
      if (!row) {
        this.userClearCurrentRow = true;
      }
      this.$refs.table.setCurrentRow(row);
    },
    triggerUpdate() {
      this.tableData.push({});
      this.tableData.pop();
    },
    tableRowClassName({ row }) {
      let computedClasses = 'line-row';
      if (!row.finishes || row.finishes.length === 0) {
        computedClasses += ' no-finishes';
      }
      return computedClasses;
    },
    tableCellClassName({ row }) {
      const computedClasses = 'line-cell';
      return computedClasses;
    },
    mustShowFormula(row) {
      return row.isFinish && row.calculationMethodOption !== QuantityCalculationMethodOption.NotApplicable;
    },
    isFinishType(row) {
      return row && row.isFinishType;
    },
    isFinish(row) {
      return row && row.isFinish;
    }
  }
};
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.finish-types-component {
  .el-checkbox {
    margin-right: 15px;
  }
}
.finishtypes-finishes-container {
  .buttons-container {
    margin-bottom: 20px;
    .top-buttons {
      text-align: right;
      height: 50px;
      overflow: hidden;
      .el-button {
        font-size: 19px;
      }
    }
  }
  .cell {
    word-break: break-word;
  }
  .child-selected {
    .code {
      color: $-colorPrimary;
      font-weight: bold;
    }
  }
  .selected {
    td {
      background-color: #fce6e9;
    }
  }
  .invisible {
    opacity: 0;
  }
  .textline-row {
    color: #909399;
  }
  .line-row {
    .cell {
      white-space: nowrap;
    }
    &.no-finishes {
      .line-cell:nth-child(1) {
        padding-left: 16px;
      }
    }
  }
  .on-clipboard {
    background-color: grey !important;
  }
  .el-table__expand-icon {
    display: inline-block;
  }
  .is-calculated input {
    font-style: italic;
  }
}
</style>
